import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Cube, MagnifyingGlass, Check, X, Eye, Funnel
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCatalogPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', qc_status: '', seller_id: '', search: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [qcNotes, setQcNotes] = useState('');

  useEffect(() => { fetchData(); }, [filters.category, filters.qc_status, filters.seller_id]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.qc_status) params.append('qc_status', filters.qc_status);
      if (filters.seller_id) params.append('seller_id', filters.seller_id);
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/products?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data.filter(c => c.level === 1));
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleQc = async (productId, status) => {
    try {
      await axios.post(`${API_URL}/api/admin/products/${productId}/qc`, { qc_status: status, notes: qcNotes }, { withCredentials: true });
      toast.success(`Product ${status}`);
      setSelectedProduct(null);
      setQcNotes('');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const filteredProducts = products.filter(p => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.sku?.toLowerCase().includes(s) || p.seller_name?.toLowerCase().includes(s);
  });

  const qcColor = (s) => s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning';

  const columns = [
    { key: 'sku', label: 'SKU', render: v => <span className="font-mono text-xs">{v}</span> },
    { key: 'name', label: 'Product Name' },
    { key: 'seller_name', label: 'Seller', render: (v, row) => (
      <div><p className="text-sm font-medium">{v}</p><p className="text-[10px] text-[#9c9db7]">{row.seller_email}</p></div>
    )},
    { key: 'category', label: 'Category' },
    { key: 'price', label: 'Price', render: v => fmt(v) },
    { key: 'mrp', label: 'MRP', render: v => <span className="text-[#9c9db7]">{fmt(v)}</span> },
    { key: 'qc_status', label: 'QC Status', render: v => <span className={`px-2 py-0.5 text-xs font-semibold rounded sp-badge-${qcColor(v)} capitalize`}>{v}</span> },
    { key: 'is_active', label: 'Active', render: v => v ? <Check size={16} className="text-[#28cd8c]" /> : <X size={16} className="text-[#ea4b5a]" /> },
    { key: 'product_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedProduct(row); setQcNotes(''); }} data-testid={`review-product-${v}`}><Eye size={15} /></Button>
        {row.qc_status === 'pending' && (
          <>
            <Button variant="ghost" size="sm" className="text-[#28cd8c]" onClick={() => handleQc(v, 'approved')} data-testid={`approve-product-${v}`}><Check size={15} /></Button>
            <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleQc(v, 'rejected')} data-testid={`reject-product-${v}`}><X size={15} /></Button>
          </>
        )}
      </div>
    )}
  ];

  // Summary cards
  const totalProducts = products.length;
  const pendingQc = products.filter(p => p.qc_status === 'pending').length;
  const approvedProducts = products.filter(p => p.qc_status === 'approved').length;
  const uniqueSellers = new Set(products.map(p => p.seller_id)).size;

  return (
    <DashboardLayout title="Product Catalog" subtitle="Review and manage all seller products">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Products', value: totalProducts, color: '#7d64ff' },
          { label: 'Pending QC', value: pendingQc, color: '#f6a609' },
          { label: 'Approved', value: approvedProducts, color: '#28cd8c' },
          { label: 'Sellers', value: uniqueSellers, color: '#5a5c87' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-[#e5e5ea] rounded-xl p-4" data-testid={`catalog-stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className="text-xs text-[#9c9db7] font-medium uppercase tracking-wider">{card.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e5e5ea] mb-6">
        <div className="action-bar">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
            <Input placeholder="Search by SKU, Name, or Seller..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} className="pl-10 rounded-sm" data-testid="catalog-search" />
          </div>
          <Select value={filters.category || "all"} onValueChange={v => setFilters({ ...filters, category: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="catalog-category-filter"><Funnel size={14} className="mr-1" /><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.qc_status || "all"} onValueChange={v => setFilters({ ...filters, qc_status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="catalog-qc-filter"><SelectValue placeholder="QC Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All QC</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : (
        <SortableTable columns={columns} data={filteredProducts} pageSize={15} emptyMessage="No products found" emptyIcon={Cube} />
      )}

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Product Review</DialogTitle></DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#9c9db7] text-xs uppercase">SKU</span><p className="font-mono font-medium">{selectedProduct.sku}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">Name</span><p className="font-medium">{selectedProduct.name}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">Category</span><p>{selectedProduct.category}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">Brand</span><p>{selectedProduct.brand || '-'}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">Price</span><p className="font-medium">{fmt(selectedProduct.price)}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">MRP</span><p>{fmt(selectedProduct.mrp)}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">Seller</span><p>{selectedProduct.seller_name}</p></div>
                <div><span className="text-[#9c9db7] text-xs uppercase">QC Status</span><p className="capitalize font-semibold">{selectedProduct.qc_status}</p></div>
              </div>
              {selectedProduct.description && <div><span className="text-[#9c9db7] text-xs uppercase">Description</span><p className="text-sm mt-1">{selectedProduct.description}</p></div>}
              {selectedProduct.qc_status === 'pending' && (
                <div className="space-y-3 pt-2 border-t border-[#f0f0f0]">
                  <Input placeholder="QC Notes (optional)" value={qcNotes} onChange={e => setQcNotes(e.target.value)} className="rounded-sm" data-testid="qc-notes-input" />
                  <div className="flex gap-3">
                    <Button className="flex-1 bg-[#28cd8c] hover:bg-[#1fb076] text-white" onClick={() => handleQc(selectedProduct.product_id, 'approved')} data-testid="qc-approve-btn">Approve</Button>
                    <Button className="flex-1 bg-[#ea4b5a] hover:bg-[#c43447] text-white" onClick={() => handleQc(selectedProduct.product_id, 'rejected')} data-testid="qc-reject-btn">Reject</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
